/**
 * @fileoverview PDF API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the PDF module.
 * Handles PDF generation for test results and analysis.
 */

import { z } from 'zod';
import {
	createContract,
	ApiSuccessResponseSchema,
} from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * PDF Type Enum
 */
export enum PdfType {
	QUICK = 'quick', // Test results only
	FULL = 'full', // Test results + analysis
}

/**
 * PDF Status Enum
 */
export enum PdfStatus {
	PENDING = 'pending',
	GENERATING = 'generating',
	GENERATED = 'generated',
	FAILED = 'failed',
}

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * PDF Type Schema
 */
export const PdfTypeSchema = z.enum(PdfType);

/**
 * PDF Status Schema
 */
export const PdfStatusSchema = z.enum(PdfStatus);

/**
 * Generate PDF Request Schema
 */
export const GeneratePdfRequestSchema = z.object({
	testId: z.uuid(),
	pdfType: PdfTypeSchema.default(PdfType.FULL),
	includeAnalysis: z.boolean().default(true),
});
export type GeneratePdfRequest = z.infer<typeof GeneratePdfRequestSchema>;

/**
 * Generate PDF Preview Request Schema
 * Used for generating PDF preview without saving to disk
 * Includes optional reviewId for loading snapshot data
 */
export const GeneratePdfPreviewRequestSchema = z.object({
	testId: z.string().min(1, 'Test ID is required'), // Format: test_<uuid>
	reviewId: z.uuid().optional(), // Optional: load snapshot data if provided
	pdfType: PdfTypeSchema.default(PdfType.FULL),
	includeAnalysis: z.boolean().default(true),
});
export type GeneratePdfPreviewRequest = z.infer<typeof GeneratePdfPreviewRequestSchema>;

/**
 * Generate PDF Preview Data Schema (inner data, not wrapped)
 * Returns base64-encoded PDF for inline preview
 */
export const GeneratePdfPreviewDataSchema = z.object({
	pdfBase64: z.string().describe('Base64-encoded PDF content for preview'),
	filename: z.string(),
	message: z.string(),
});
export type GeneratePdfPreviewData = z.infer<typeof GeneratePdfPreviewDataSchema>;

/**
 * Generate PDF Preview Response Schema (wrapped with success/timestamp)
 * { success: true, data: GeneratePdfPreviewData, timestamp: string }
 */
export const GeneratePdfPreviewResponseSchema = ApiSuccessResponseSchema(GeneratePdfPreviewDataSchema);
export type GeneratePdfPreviewResponse = z.infer<typeof GeneratePdfPreviewResponseSchema>;

/**
 * Generate PDF Data Schema (inner data, not wrapped)
 */
export const GeneratePdfDataSchema = z.object({
	pdfId: z.uuid().optional(),
	pdfUrl: z.string().url().optional(),
	message: z.string(),
});
export type GeneratePdfData = z.infer<typeof GeneratePdfDataSchema>;

/**
 * Generate PDF Response Schema (wrapped with success/timestamp)
 * { success: true, data: GeneratePdfData, timestamp: string }
 */
export const GeneratePdfResponseSchema = ApiSuccessResponseSchema(GeneratePdfDataSchema);
export type GeneratePdfResponse = z.infer<typeof GeneratePdfResponseSchema>;

/**
 * Download PDF Response Schema
 * Note: This returns a Buffer, not JSON
 *
 * We use z.any() for buffer because Buffer is not available in browser environments
 * The backend will validate this is actually a Buffer at runtime
 */
export const DownloadPdfResponseSchema = z.object({
	buffer: z.any().describe('PDF file buffer (Node.js Buffer)'),
	filename: z.string(),
	contentType: z.string(),
});
export type DownloadPdfResponse = z.infer<typeof DownloadPdfResponseSchema>;

/**
 * PDF Metadata Schema
 *
 * and ISO strings (from API). This allows @ZodResponse to work correctly with DB entities.
 */
export const PdfMetadataSchema = z.object({
	id: z.uuid(),
	testId: z.uuid(),
	testResultId: z.uuid(),
	pdfType: PdfTypeSchema,
	status: PdfStatusSchema,
	filename: z.string(),
	fileSize: z.number().int().nonnegative().optional(),
	filePath: z.string().optional(),
	includesAnalysis: z.boolean(),
	generatedAt: z.iso.datetime().optional(),
	error: z.string().optional(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type PdfMetadata = z.infer<typeof PdfMetadataSchema>;

/**
 * PDF Generation Options Schema
 */
export const PdfGenerationOptionsSchema = z.object({
	includeHeader: z.boolean().default(true),
	includeFooter: z.boolean().default(true),
	includeScores: z.boolean().default(true),
	includeAnalysis: z.boolean().default(true),
	includeRecommendations: z.boolean().default(true),
	language: z.enum(['de', 'en']).default('de'),
});
export type PdfGenerationOptions = z.infer<typeof PdfGenerationOptionsSchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

/**
 * Generate PDF API Response
 * { success: true, data: GeneratePdfData, timestamp: string }
 */
export const GeneratePdfApiResponseSchema = GeneratePdfResponseSchema;

/**
 * Generate PDF Preview API Response
 * { success: true, data: GeneratePdfPreviewData, timestamp: string }
 */
export const GeneratePdfPreviewApiResponseSchema = GeneratePdfPreviewResponseSchema;

/**
 * PDF Metadata API Response
 * { success: true, data: PdfMetadata, timestamp: string }
 */
export const PdfMetadataApiResponseSchema = ApiSuccessResponseSchema(PdfMetadataSchema);

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * PDF API Contract
 *
 * Defines the complete API surface for the PDF module.
 * Both frontend and backend use this as the single source of truth.
 */
export const PDF_API_CONTRACT = createContract('/pdf', {
	/**
	 * POST /pdf/generate
	 * Generate a PDF for test results
	 */
	generate: {
		method: 'POST' as const,
		path: '/generate',
		body: GeneratePdfRequestSchema,
		response: GeneratePdfResponseSchema,
	},

	/**
	 * POST /pdf/preview
	 * Generate a PDF preview (base64-encoded, not saved to disk)
	 * Used for inline preview before approval
	 */
	preview: {
		method: 'POST' as const,
		path: '/preview',
		body: GeneratePdfPreviewRequestSchema,
		response: GeneratePdfPreviewResponseSchema,
	},

	/**
	 * GET /pdf/:testId/download
	 * Download PDF for a test result
	 * Note: Returns binary data (application/pdf)
	 */
	download: {
		method: 'GET' as const,
		path: '/:testId/download',
		params: z.object({
			testId: z.uuid(),
		}),
		query: z
			.object({
				pdfType: PdfTypeSchema.optional(),
			})
			.optional(),
		response: z.any(), // Binary response, not JSON
	},

	/**
	 * GET /pdf/:testId/metadata
	 * Get PDF metadata for a test result
	 */
	getMetadata: {
		method: 'GET' as const,
		path: '/:testId/metadata',
		params: z.object({
			testId: z.uuid(),
		}),
		response: PdfMetadataSchema,
	},

	/**
	 * DELETE /pdf/:pdfId
	 * Delete a generated PDF
	 */
	delete: {
		method: 'DELETE' as const,
		path: '/:pdfId',
		params: z.object({
			pdfId: z.uuid(),
		}),
		response: z.object({
			success: z.boolean(),
			message: z.string(),
		}),
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const PDF_ROUTES = {
	base: PDF_API_CONTRACT.base,
	generate: PDF_API_CONTRACT.routes.generate.path,
	preview: PDF_API_CONTRACT.routes.preview.path,
	download: PDF_API_CONTRACT.routes.download.path,
	getMetadata: PDF_API_CONTRACT.routes.getMetadata.path,
	delete: PDF_API_CONTRACT.routes.delete.path,
};

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type PdfApiContract = typeof PDF_API_CONTRACT;
